import {
  UserActivityStatus,
  UserAvailabilityStatus,
  UserSetAvailabilityStatus,
} from '@api/generated/graphql';
import ServiceUser from '../types/ServiceUser';
import { EventAvailabilityState } from './eventAvailabilityState';
import { isIsoBefore } from '@lib/utils/time';
import { calculateConsolidatedStatus } from '@modules/UserStatusModule/utilities/ConsolidatedStatus/ConsolidatedStatus';

type UserAvailableResponse =
  | { isAvailable: true; isUnavailable: false; isOffline: false }
  | { isAvailable: false; isUnavailable: true; isOffline: false }
  | { isAvailable: false; isUnavailable: false; isOffline: true };

// Utility functions for ServiceUser
export const ServiceUserUtils = {
  setAvailabilityStatus(user: ServiceUser, status: UserSetAvailabilityStatus) {
    user.availabilityStatus = status;
  },

  getAvailabilityStatus(userStatus: {
    availabilityStatus: UserSetAvailabilityStatus;
  }): UserSetAvailabilityStatus {
    return userStatus.availabilityStatus;
  },

  isUserAvailable(user: ServiceUser): boolean {
    return (
      this.getAvailabilityStatus(user) === UserSetAvailabilityStatus.Available
    );
  },

  getActivityStatus(userStatus: ServiceUser): UserActivityStatus {
    return userStatus.activityStatus;
  },

  setActivityStatus(user: ServiceUser, activityStatus: UserActivityStatus) {
    user.activityStatus = activityStatus;
  },

  isWrapUp(user: ServiceUser): boolean {
    return user.activityStatus === UserActivityStatus.WrapUp;
  },

  setEventActivityStatus(
    user: ServiceUser,
    eventAvailabilityState: string,
    setActivityStatus: boolean = true,
  ) {
    if (setActivityStatus) {
      user.activityStatus = convertEventAvailabilityStateToActivity(
        eventAvailabilityState,
      );
    }
  },

  parseAvailabilityActivityStatus(user: {
    availabilityActivityStatus?: {
      availabilityStatus?: UserAvailabilityStatus | null;
      activityStatus?: UserActivityStatus | null;
    } | null;
  }) {
    if (
      user.availabilityActivityStatus?.availabilityStatus &&
      user.availabilityActivityStatus?.activityStatus
    ) {
      return {
        activityStatus: user.availabilityActivityStatus.activityStatus,
        availabilityStatus: userStatusToUserSetStatus(
          user.availabilityActivityStatus.availabilityStatus,
        ),
      };
    } else {
      console.error(
        'parseAvailabilityActivityStatus: availabilityActivityStatus is not returned from the query, user ',
        user,
      );
      return {
        activityStatus: UserActivityStatus.None,
        availabilityStatus: UserSetAvailabilityStatus.Offline,
      };
    }
  },

  parseAvailabilityStatus(user: {
    availabilityActivityStatus?: {
      availabilityStatus?: UserAvailabilityStatus | null;
      activityStatus?: UserActivityStatus | null;
    } | null;
  }) {
    const status =
      user.availabilityActivityStatus?.availabilityStatus ??
      UserAvailabilityStatus.Offline;
    return userStatusToUserSetStatus(status);
  },

  getUserCapacity(user: ServiceUser): number {
    return user.activityStatus === UserActivityStatus.OnACall ||
      user.activityStatus === UserActivityStatus.WrapUp
      ? 0
      : 1;
  },

  /**
   * Checks if a service user is available
   * @param user - The service user to check
   * @returns The availability status of the service user as type {@link UserAvailableResponse}.
   *          Contains a boolean flag indicating if the user is available, unavailable, and offline (these are all mutually exclusive)
   */
  computeUserAvailability(user: ServiceUser): UserAvailableResponse {
    const status = ServiceUserUtils.getAvailabilityStatus(user);
    const activity = ServiceUserUtils.getActivityStatus(user);

    const isAvailable =
      status === UserSetAvailabilityStatus.Available &&
      (activity === UserActivityStatus.None ||
        activity === UserActivityStatus.CallOffer);

    if (isAvailable) {
      return {
        isUnavailable: false,
        isOffline: false,
        isAvailable: true,
      };
    }

    const isOffline = status === UserSetAvailabilityStatus.Offline;
    if (isOffline) {
      return {
        isUnavailable: false,
        isAvailable: false,
        isOffline: true,
      };
    }

    return {
      isAvailable: false,
      isUnavailable: true,
      isOffline: false,
    };
  },

  getUserConsolidatedStatus(user: ServiceUser) {
    return calculateConsolidatedStatus(user);
  },

  isCallOffered(user: ServiceUser): boolean {
    return (
      ServiceUserUtils.getActivityStatus(user) === UserActivityStatus.CallOffer
    );
  },
};

export const convertEventAvailabilityStateToActivity = (
  eventState: string,
): UserActivityStatus => {
  switch (eventState) {
    case EventAvailabilityState.OnACall:
      return UserActivityStatus.OnACall;
    case EventAvailabilityState.WrapupTime:
      return UserActivityStatus.WrapUp;
    case EventAvailabilityState.CallOffer:
      return UserActivityStatus.CallOffer;
    case EventAvailabilityState.OnExternalCall:
      return UserActivityStatus.OnExternalCall;
    default:
      return UserActivityStatus.None;
  }
};

export function userStatusToUserSetStatus(
  status: UserAvailabilityStatus,
): UserSetAvailabilityStatus {
  switch (status) {
    case UserAvailabilityStatus.CallOffer:
    case UserAvailabilityStatus.OnACall:
    case UserAvailabilityStatus.WrapUp:
    case UserAvailabilityStatus.Available:
      return UserSetAvailabilityStatus.Available;
    case UserAvailabilityStatus.AwayFromDesk:
      return UserSetAvailabilityStatus.AwayFromDesk;
    case UserAvailabilityStatus.Break:
      return UserSetAvailabilityStatus.Break;
    case UserAvailabilityStatus.Offline:
      return UserSetAvailabilityStatus.Offline;
    case UserAvailabilityStatus.ExtendedWrapUp:
      return UserSetAvailabilityStatus.ExtendedWrapUp;
    case UserAvailabilityStatus.Unavailable:
      return UserSetAvailabilityStatus.Unavailable;
  }
}

/**
 * Updates `user.stats.lastActionAt` only when the provided `timestamp` is later
 * than the user's current `lastActionAt` value.
 *
 * Behavior:
 * - No-op if `user.stats?.lastActionAt` is absent.
 * - No-op if `timestamp` is falsy or not later than the current `lastActionAt`.
 * - Mutates `user.stats.lastActionAt` in place when `timestamp` is a valid ISO string
 *   that represents a later instant.
 *
 * @param user - The `ServiceUser` whose stats will be updated in place.
 * @param timestamp - ISO-8601 timestamp (string) derived from the current event/action.
 * @returns void
 */
export const userStatsLastActionTimeFix = ({
  user,
  timestamp,
}: {
  user: ServiceUser;
  timestamp: string;
}) => {
  if (!user.stats?.lastActionAt) return;

  const { lastActionAt } = user.stats;

  // if current action timestamp is later than what we have, update it
  // The fix due to events don't have latest lastActionTime https://servicetitan.atlassian.net/browse/CCP-16049
  if (timestamp && isIsoBefore(lastActionAt, timestamp)) {
    user.stats.lastActionAt = timestamp;
  }
};
